// src/components/features/departures/useAutoRefreshCountdown.ts
import { useEffect, useState } from 'preact/hooks';

interface UseAutoRefreshCountdownOptions {
  autoRefreshEnabled: boolean;
  lastUpdatedAt: number | null;
  refreshIntervalSeconds?: number;
}

const DEFAULT_REFRESH_INTERVAL_SECONDS = 60;

/**
 * Calculates a second-by-second countdown to the next departures refresh tick.
 */
export function useAutoRefreshCountdown(
  options: UseAutoRefreshCountdownOptions,
): number | null {
  const {
    autoRefreshEnabled,
    lastUpdatedAt,
    refreshIntervalSeconds = DEFAULT_REFRESH_INTERVAL_SECONDS,
  } = options;
  const [nowTimestamp, setNowTimestamp] = useState(() => Date.now());
  const [prevAutoRefreshEnabled, setPrevAutoRefreshEnabled] =
    useState(autoRefreshEnabled);

  useEffect(() => {
    // Detect transition from off to on
    if (autoRefreshEnabled && !prevAutoRefreshEnabled) {
      setNowTimestamp(Date.now());
    }
    setPrevAutoRefreshEnabled(autoRefreshEnabled);
  }, [autoRefreshEnabled, prevAutoRefreshEnabled]);

  useEffect(() => {
    if (!autoRefreshEnabled || lastUpdatedAt === null) {
      return;
    }

    const timerId = globalThis.setInterval(() => {
      setNowTimestamp(Date.now());
    }, 1000);

    return () => {
      globalThis.clearInterval(timerId);
    };
  }, [autoRefreshEnabled, lastUpdatedAt]);

  return autoRefreshEnabled && lastUpdatedAt !== null
    ? Math.max(
        0,
        refreshIntervalSeconds -
          Math.floor((nowTimestamp - lastUpdatedAt) / 1000),
      )
    : null;
}
